import { Action } from 'redux'
import { PayloadAction } from '@reduxjs/toolkit'
import { EventChannel, eventChannel } from 'redux-saga'
import { call, fork, put, take, takeLatest } from 'redux-saga/effects'
import { Socket } from 'socket.io-client'

import { ChatRoom } from '@/core/models/chat-room.model'
import { Consultation } from '@/core/models/consultation.model'
import { Room } from '@/core/models/room.model'
import { OnlineStaff } from '@/core/classes/Auth'
import { Staff } from '@/core/models/staff.model'
import { WaitingRoom } from '@/core/models/waiting-room.model'

import { online, onlineList, onlineUsers } from '@/redux/reducers/auth.reducer'
import { chatMessageFromSocket } from '@/redux/reducers/chat-room.reducer'
import {
  consultationCreatedSocketEvent,
  consultationUpdatedSocketEvent,
  joinConsultationSocketEvent,
  leaveConsultationSocketEvent,
  createConsultationSuccess,
  updateConsultationSuccess,
  sendRpiIdSocketEvent,
  getRpiMeetingIdSuccess,
  requestRpiIdSocketEvent,
  receiveRpiIdRequest,
  participantsList,
  sendMesuresSocketEvent,
  recordingConsentRequestedSocketEvent,
  recordingConsentResponseReceived,
} from '@/redux/reducers/consultation.reducer'
import { addDictaphoneDocumentFailure, addDictaphoneDocumentSuccess } from '@/redux/reducers/document.reducer'
import { patientUpdatedSocketEvent } from '@/redux/reducers/patient.reducer'
import {
  emergencyRoomActiveSocketEvent,
  emergencyRoomInactiveSocketEvent,
  emergencyRoomUpdatedSocketEvent,
  enterEmergencyRoomSocketEvent,
  leaveEmergencyRoomSocketEvent,
  updateRoomSuccess,
} from '@/redux/reducers/room.reducer'
import { getStaff, updateStaffSuccess } from '@/redux/reducers/staff.reducer'
import {
  enterWaitingRoomSocketEvent,
  leaveWaitingRoomSocketEvent,
  updateWaitingRoomSuccess,
  doctorHeartbeatSocketEvent,
  doctorStatusUpdated,
  waitingRoomDoctorsStatusReceived
} from '@/redux/reducers/waiting-room.reducer'

import { getSocket } from '@/core/api/socket'
import { SocketEvent } from '@/core/types/socket-event'

// TODO: re-organize event name, should be underscore case
function createSocketChannel(socket: Socket) {
  return eventChannel((emit) => {
    // backward compatibility
    // TODO: change event name to leave_consultation
    socket.on('leavedConsultation', (consultation: Consultation) => {
      emit(updateConsultationSuccess(consultation))
    })
    // socket.on('consultationPeoples', (reason: string) => {

    // })
    // socket.on('liveActions', (reason: string) => {
    //   console.log('socket disconnected', reason)
    // })
    // socket.on('consultationScheduleEvent', ((res: { action: string, consultation: Consultation }) => {

    // }))

    socket.on('consultation_created', (consultation: Consultation) => {
      emit(createConsultationSuccess(consultation))
    })

    socket.on('consultation_updated', (consultation: Consultation) => {
      emit(updateConsultationSuccess(consultation))
    })

    socket.on('consultation_dictaphone_updated', (consultation: Consultation) => {
      if (consultation === null) {
        emit(addDictaphoneDocumentFailure())
      } else {
        emit(updateConsultationSuccess(consultation))
        emit(addDictaphoneDocumentSuccess())
      }
    })

    socket.on('consultation_participants', (consultationId: string, participants: Staff[]) => {
      emit(participantsList(consultationId, participants))
    })

    socket.on('emergency_room_updated', (staffId: string, room: Room) => {
      emit(getStaff(staffId))
      emit(updateRoomSuccess(room))
    })

    socket.on('onlineList', (onlineStaffs: OnlineStaff[]) => {
      emit(onlineList(onlineStaffs))
    })

    socket.on('online_users', (users: string[]) => {
      emit(onlineUsers(users))
    })

    socket.on('request_rpi_id', (consultationId: string) => {
      emit(receiveRpiIdRequest(consultationId))
    })

    socket.on('room_updated', (room: Room) => {
      emit(updateRoomSuccess(room))
    })

    socket.on('rpi_id', (rpiId: string) => {
      emit(getRpiMeetingIdSuccess(rpiId))
    })

    socket.on('staff_updated', (staff: Staff) => {
      emit(updateStaffSuccess(staff))
    })

    socket.on('waiting_room_updated', (waitingRoom: WaitingRoom) => {
      emit(updateWaitingRoomSuccess(waitingRoom))
    })

    socket.on('chat_room', (chatRoom: ChatRoom) => {
      emit(chatMessageFromSocket(chatRoom))
    })

    // Doctor status listeners
    socket.on(SocketEvent.DOCTOR_STATUS_UPDATED, (data: {
      doctorId: string,
      status: 'available' | 'in_consultation' | 'offline',
      consultationId?: string
    }) => {
      emit(doctorStatusUpdated(data))
    })

    socket.on(SocketEvent.WAITING_ROOM_DOCTORS_STATUS, (data: {
      doctorId: string,
      status: 'available' | 'in_consultation' | 'offline',
      consultationId?: string
    }[]) => {
      emit(waitingRoomDoctorsStatusReceived(data))
    })

    socket.on(SocketEvent.RECORDING_CONSENT_RESPONSED, (data: { consultationId: string; accepted: boolean }) => {
      emit(recordingConsentResponseReceived(data))
    })

    return () => { }
  })
}

function* listenEvent(socket: Socket) {
  const channel: EventChannel<any> = yield call(createSocketChannel, socket)
  while (true) {
    const action: Action = yield take(channel)
    yield put(action)
  }
}

function sendMessage(socket: Socket, eventName: string, action: PayloadAction<any>) {
  socket.emit(eventName, action.payload)
}

function* flow() {
  const socket: Socket = yield call(getSocket)
  yield fork(listenEvent, socket)
  yield takeLatest(consultationCreatedSocketEvent.type, sendMessage, socket, SocketEvent.CONSULTATION_CREATED) // payload of createConsultationSuccess action is consultation created
  yield takeLatest(consultationUpdatedSocketEvent.type, sendMessage, socket, SocketEvent.CONSULTATION_UPDATED)
  yield takeLatest(emergencyRoomUpdatedSocketEvent.type, sendMessage, socket, 'emergency_room_updated')
  yield takeLatest(joinConsultationSocketEvent.type, sendMessage, socket, SocketEvent.ENTER_CONSULTATION)
  yield takeLatest(leaveConsultationSocketEvent.type, sendMessage, socket, SocketEvent.LEAVE_CONSULTATION)
  yield takeLatest(online.type, sendMessage, socket, 'online') // payload of online action is auth user id
  yield takeLatest(patientUpdatedSocketEvent.type, sendMessage, socket, 'patient_updated')
  yield takeLatest(requestRpiIdSocketEvent.type, sendMessage, socket, 'request_rpi_id')
  yield takeLatest(sendRpiIdSocketEvent.type, sendMessage, socket, 'send_rpi_id')
  yield takeLatest(enterWaitingRoomSocketEvent.type, sendMessage, socket, SocketEvent.ENTER_WAITING_ROOM)
  yield takeLatest(leaveWaitingRoomSocketEvent.type, sendMessage, socket, SocketEvent.LEAVE_WAITING_ROOM)
  yield takeLatest(doctorHeartbeatSocketEvent.type, sendMessage, socket, SocketEvent.DOCTOR_HEARTBEAT)
  yield takeLatest(sendMesuresSocketEvent.type, sendMessage, socket, 'send_mesures')
  yield takeLatest(enterEmergencyRoomSocketEvent.type, sendMessage, socket, SocketEvent.ENTER_EMERGENCY_ROOM)
  yield takeLatest(leaveEmergencyRoomSocketEvent.type, sendMessage, socket, SocketEvent.LEAVE_EMERGENCY_ROOM)
  yield takeLatest(emergencyRoomActiveSocketEvent.type, sendMessage, socket, 'emergency_room_active')
  yield takeLatest(emergencyRoomInactiveSocketEvent.type, sendMessage, socket, 'emergency_room_inactive')
  yield takeLatest(recordingConsentRequestedSocketEvent.type, sendMessage, socket, SocketEvent.RECORDING_CONSENT_REQUESTED)
}

export default fork(flow)
